// src/components/User/UserMenu.jsx

import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import urlConfig from '../../data/UrlConfig';
import { getApi, postApi } from '../../services/apiService';
import { staticContentConfig } from '../../data/staticContentConfig';

import {
  Bars3Icon,
  XMarkIcon,
  ArrowRightOnRectangleIcon,
  GiftIcon,
  BoltIcon,
  SparklesIcon,
  HomeIcon,
  LanguageIcon,
  StarIcon,
  Cog6ToothIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/solid';

const noAuthMenuRoutes = [
  '/login', '/signup', '/en/login', '/en/signup',
  '/es/login', '/es/signup', '/going-gamma', '/gamma', '/verify-email',
  '/forgot-password', '/reset-password', '/checkout', '/gamma-activation-kit',
  '/oraculo-portal', '/oracle-portal', '/daily-oracle', '/portal-oracle',
  '/oraculo', '/oracle',
];

const authMenuRoutes = [
  '/es/menu', '/menu',
  '/en/menu',
  '/bashar/menu'
];

// Diccionario de traducción para categorías y títulos comunes
const translationsDict = {
  // Categorías
  'gifts': 'Regalos',
  'gift': 'Regalos',
  'premium': 'Premium',
  'portals': 'Portales',
  'portal': 'Portales',
  'tools': 'Herramientas',
  'meditations': 'Meditaciones',
  'activations': 'Activaciones',
  'courses': 'Cursos',
  'sessions': 'Sesiones',
  'audios': 'Audios',
  'books': 'Libros',
  'resources': 'Recursos',
  'oracles': 'Oráculos',
  'oracle': 'Oráculo',

  // Rutas / Vistas generales
  'dashboard': 'Panel Principal'
};

const ProductIcon = ({ type }) => {
  switch (type) {
    case 'premium': return <StarIcon className="w-6 h-6 text-yellow-400 flex-shrink-0" />;
    case 'gift': return <GiftIcon className="w-6 h-6 text-yellow-500 flex-shrink-0" />;
    case 'air': return <BoltIcon className="w-6 h-6 text-cyan-400 flex-shrink-0" />;
    case 'ohm': return <SparklesIcon className="w-6 h-6 text-purple-400 flex-shrink-0" />;
    default: return <GiftIcon className="w-6 h-6 text-yellow-500 flex-shrink-0" />;
  }
};

const TitledSeparator = ({ title }) => (
  <div className="relative my-3">
    <div className="absolute inset-0 flex items-center" aria-hidden="true">
      <div className="w-full border-t border-gray-700" />
    </div>
    <div className="relative flex justify-center">
      <span className="bg-gray-900 px-3 text-xs font-bold text-gray-500 uppercase tracking-wider">
        {title}
      </span>
    </div>
  </div>
);

const UserMenu = () => {
  const { user, logout, setUser } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [showLangNotice, setShowLangNotice] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const [userProducts, setUserProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Determinar si la vista actual está en español
  const isSpanish = useMemo(() => {
    return user?.lang == 'es';
  }, [location.pathname, user]);

  // Helper para obtener el texto localizado
  const getLocalizedTitle = (itemOrTitle) => {
    if (!itemOrTitle) return '';

    // Si se pasa un string directamente (ej: nombre de categoría)
    if (typeof itemOrTitle === 'string') {
      if (isSpanish) {
        return translationsDict[itemOrTitle.toLowerCase()] || itemOrTitle;
      }
      return itemOrTitle;
    }

    // Si es un objeto (producto o item de menú)
    if (isSpanish) {
      if (typeof itemOrTitle.title === 'object') {
        return itemOrTitle.title.es || itemOrTitle.title.en;
      }
      return (
        itemOrTitle.titleEs ||
        itemOrTitle.title_es ||
        translationsDict[itemOrTitle.title?.toLowerCase()] ||
        itemOrTitle.title
      );
    }

    // En inglés
    if (typeof itemOrTitle.title === 'object') {
      return itemOrTitle.title.en || itemOrTitle.title.es;
    }
    return itemOrTitle.title;
  };

  // Estado del formulario de configuración
  const [selectedLanguage, setSelectedLanguage] = useState(isSpanish ? 'es' : 'en');
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [settingsFeedback, setSettingsFeedback] = useState({ type: null, message: '' });

  // Sincronizar el idioma seleccionado cuando cambie la ruta o el usuario
  useEffect(() => {
    setSelectedLanguage(isSpanish ? 'es' : 'en');
  }, [isSpanish]);

  // Categorización de productos
  const categorizedProducts = useMemo(() => {
    return {
      premium: userProducts.filter(p => p.type === 'premium'),
      gifts: userProducts.filter(p => p.type === 'gift'),
      other: userProducts.filter(p => p.type !== 'premium' && p.type !== 'gift')
    };
  }, [userProducts]);

  // Configuración de cambio de idioma según la ruta actual
  const langNoticeConfig = useMemo(() => {
    const pathname = location.pathname;

    if (pathname === '/es/menu' || pathname.startsWith('/es')) {
      const target = pathname.startsWith('/es') 
        ? pathname.replace(/^\/es/, '/en') 
        : '/en';

      return {
        storageKey: 'dismissedLangNotice_to_en',
        headerText: 'Language',
        actionText: 'Switch to English',
        targetPath: target
      };
    }

    if (pathname === '/en/menu' || pathname.startsWith('/en')) {
      const target = pathname.startsWith('/en') 
        ? pathname.replace(/^\/en/, '/es') 
        : '/es';

      return {
        storageKey: 'dismissedLangNotice_to_es',
        headerText: 'Idioma',
        actionText: 'Cambiar a Español',
        targetPath: target
      };
    }

    return null;
  }, [location.pathname]);

  // Manejo del timer y visibilidad del aviso
  useEffect(() => {
    if (!langNoticeConfig) {
      setShowLangNotice(false);
      setIsAnimating(false);
      return;
    }

    const hasDismissed = localStorage.getItem(langNoticeConfig.storageKey);
    if (!hasDismissed) {
      const timer = setTimeout(() => {
        setShowLangNotice(true);
        setIsAnimating(true);
      }, 1500);
      return () => clearTimeout(timer);
    } else {
      setShowLangNotice(false);
      setIsAnimating(false);
    }
  }, [langNoticeConfig]);

  useEffect(() => {
    if (!user) { setUserProducts([]); return; }
    const fetchUserProducts = async () => {
      setIsLoading(true);
      try {
        const data = await getApi(urlConfig.user_get_products);
        setUserProducts(data);
      } catch (err) { setError(err.message); } finally { setIsLoading(false); }
    };
    fetchUserProducts();
  }, [user]);

  const dismissLangNotice = (storageKey) => {
    setIsAnimating(false);
    setTimeout(() => {
      if (storageKey) {
        localStorage.setItem(storageKey, 'true');
      }
      setShowLangNotice(false);
    }, 300);
  };

  const handleLogout = async () => { await logout(); navigate('/login'); };

  // Guardar configuración y actualizar en la Base de Datos
  const handleSaveSettings = async () => {
    setIsSavingSettings(true);
    setSettingsFeedback({ type: null, message: '' });

    try {
      const endpoint = urlConfig.user_settings_update;
      const payload = { lang: selectedLanguage };
      await postApi(endpoint, payload);

      setUser(prev => ({ ...prev, lang: selectedLanguage }));

      setSettingsFeedback({
        type: 'success',
        message: selectedLanguage === 'es' ? 'Preferencias guardadas' : 'Settings saved successfully'
      });

      const pathname = location.pathname;
      if (selectedLanguage === 'es' && pathname.startsWith('/en')) {
        navigate(pathname.replace(/^\/en/, '/es'));
      } else if (selectedLanguage === 'en' && pathname.startsWith('/es')) {
        navigate(pathname.replace(/^\/es/, '/en'));
      }

      setTimeout(() => {
        setIsSettingsOpen(false);
        setSettingsFeedback({ type: null, message: '' });
      }, 900);

    } catch (err) {
      setSettingsFeedback({
        type: 'error',
        message: err.message || (selectedLanguage === 'es' ? 'Error al guardar los cambios' : 'Failed to save settings')
      });
    } finally {
      setIsSavingSettings(false);
    }
  };

  const LanguageNotice = () => {
    if (!showLangNotice || !langNoticeConfig) return null;
    const positionClass = user ? 'right-4' : 'left-4';

    return (
      <div 
        className={`fixed top-4 ${positionClass} z-[100] flex items-center gap-3 p-3 bg-gray-900/90 backdrop-blur border border-white/10 rounded-xl shadow-2xl transition-all duration-500 transform ${
          isAnimating ? 'translate-y-0 opacity-100' : '-translate-y-20 opacity-0 pointer-events-none'
        }`}
      >
        <LanguageIcon className="w-6 h-6 text-indigo-400" />
        <div className="flex flex-col">
          <span className="text-[10px] text-gray-400 uppercase tracking-wider">
            {langNoticeConfig.headerText}
          </span>
          <Link 
            to={langNoticeConfig.targetPath} 
            onClick={() => dismissLangNotice(langNoticeConfig.storageKey)}
            className="text-sm font-bold text-white hover:text-indigo-300 transition-colors"
          >
            {langNoticeConfig.actionText}
          </Link>
        </div>
        <button 
          onClick={() => dismissLangNotice(langNoticeConfig.storageKey)} 
          className="ml-2 p-1 text-gray-500 hover:text-white transition-colors"
          aria-label="Cerrar aviso de idioma"
        >
          <XMarkIcon className="w-4 h-4" />
        </button>
      </div>
    );
  };

  if (!user) {
    const isExcluded = noAuthMenuRoutes.some(route => location.pathname.startsWith(route));
    if (isExcluded || !authMenuRoutes.includes(location.pathname)) return <LanguageNotice />;
    return (
      <>
        <LanguageNotice />
        <Link to={`/login?redirect=${location.pathname}`} className="fixed top-4 right-4 z-[100] flex items-center gap-2 p-2 pl-3 pr-4 border border-white/20 rounded-full text-gray-200 hover:bg-white/10 hover:text-white transition-all">
          <ArrowRightOnRectangleIcon className="w-5 h-5 transform -scale-x-100" />
          <span className="font-semibold text-sm">Acceder</span>
        </Link>
      </>
    );
  }

  return (
    <>
      <LanguageNotice />
      
      {/* Botón hamburguesa */}
      <button onClick={() => setIsMenuOpen(true)} className="fixed top-4 left-4 z-[80] p-2 bg-black/50 backdrop-blur-md rounded-full text-yellow-400 hover:bg-yellow-500 hover:text-black transition-all">
        <Bars3Icon className="w-7 h-7" />
      </button>

      {/* Backdrop del Menú Lateral */}
      <div className={`fixed inset-0 bg-black/60 z-[90] transition-opacity ${isMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} onClick={() => setIsMenuOpen(false)}></div>

      {/* Menú Lateral */}
      <div className={`fixed top-0 left-0 h-full w-72 bg-gray-900 shadow-2xl z-[95] transform transition-transform duration-300 ease-in-out ${isMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-4 border-b border-gray-700">
            <h2 className="text-xl font-bold text-white truncate">
              {isSpanish ? 'Hola,' : 'Hi,'} <span className="text-yellow-400">{user.username}</span>
            </h2>
            <button onClick={() => setIsMenuOpen(false)} className="p-1 text-gray-400 hover:text-white">
              <XMarkIcon className="w-7 h-7" />
            </button>
          </div>

          <nav className="flex-grow p-2 overflow-y-auto">
            {/* Link a Dashboard */}
            <Link to="/user/dashboard" className="flex items-center gap-3 p-3 mb-2 rounded-lg text-lg font-semibold text-yellow-400 bg-gray-800/50 hover:bg-gray-800 transition-colors" onClick={() => setIsMenuOpen(false)}>
              <HomeIcon className="w-6 h-6" /> 
              <span>{isSpanish ? 'Panel Principal' : 'Dashboard'}</span>
            </Link>

            {/* Productos Premium */}
            {categorizedProducts.premium.length > 0 && (
              <>
                <TitledSeparator title={isSpanish ? "Premium" : "Premium"} />
                {categorizedProducts.premium.map(p => (
                  <Link key={p.code} to={'/user' + p.pathUrl} className="flex items-center gap-3 p-3 rounded-lg text-lg text-gray-300 hover:bg-gray-800 hover:text-white" onClick={() => setIsMenuOpen(false)}>
                    <ProductIcon type="premium" /> <span className="truncate">{getLocalizedTitle(p)}</span>
                  </Link>
                ))}
              </>
            )}

            {/* Regalos / Gifts */}
            {categorizedProducts.gifts.length > 0 && (
              <>
                <TitledSeparator title={isSpanish ? "Regalos" : "Gifts"} />
                {categorizedProducts.gifts.map(p => (
                  <Link key={p.code} to={'/user' + p.pathUrl} className="flex items-center gap-3 p-3 rounded-lg text-lg text-gray-300 hover:bg-gray-800 hover:text-white" onClick={() => setIsMenuOpen(false)}>
                    <ProductIcon type="gift" /> <span className="truncate">{getLocalizedTitle(p)}</span>
                  </Link>
                ))}
              </>
            )}

            {/* Contenido Estático y Otras Categorías */}
            {staticContentConfig.map(({ category, items }) => (
              <React.Fragment key={category}>
                <TitledSeparator title={getLocalizedTitle(category)} />
                {items.map((item) => (
                  <Link key={item.key} to={item.path} className="flex items-center gap-3 p-3 rounded-lg text-lg text-gray-300 hover:bg-gray-800 hover:text-white" onClick={() => setIsMenuOpen(false)}>
                    <item.Icon className={`w-6 h-6 ${item.color}`} /> 
                    <span className="truncate">{getLocalizedTitle(item)}</span>
                  </Link>
                ))}
                {categorizedProducts.other.filter(p => p.category?.toLowerCase() === category.toLowerCase()).map(p => (
                  <Link key={p.code} to={'/user' + p.pathUrl} className="flex items-center gap-3 p-3 rounded-lg text-lg text-gray-300 hover:bg-gray-800 hover:text-white" onClick={() => setIsMenuOpen(false)}>
                    <ProductIcon type={p.type} /> 
                    <span className="truncate">{getLocalizedTitle(p)}</span>
                  </Link>
                ))}
              </React.Fragment>
            ))}
          </nav>

          {/* Footer del Menú: Configuración + Logout */}
          <div className="p-3 border-t border-gray-700 flex flex-col gap-1">
            <button 
              onClick={() => {
                setIsMenuOpen(false);
                setIsSettingsOpen(true);
              }} 
              className="w-full flex items-center gap-3 p-3 rounded-lg text-lg text-gray-300 hover:bg-gray-800 hover:text-white transition-colors"
            >
              <Cog6ToothIcon className="w-6 h-6 text-gray-400" />
              <span>{isSpanish ? 'Configuración' : 'Settings'}</span>
            </button>

            <button onClick={handleLogout} className="w-full flex items-center gap-3 p-3 rounded-lg text-lg text-red-400 hover:bg-red-500/20 transition-colors">
              <ArrowRightOnRectangleIcon className="w-6 h-6" />
              <span>{isSpanish ? 'Cerrar Sesión' : 'Log Out'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Modal de Configuración */}
      {isSettingsOpen && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
          <div 
            className="fixed inset-0 bg-black/75 backdrop-blur-sm transition-opacity"
            onClick={() => setIsSettingsOpen(false)}
          />

          <div className="relative w-full max-w-md bg-gray-900 border border-gray-700/80 rounded-2xl shadow-2xl p-6 z-[120] text-white">
            {/* Cabecera del Modal */}
            <div className="flex items-center justify-between pb-4 border-b border-gray-800">
              <div className="flex items-center gap-2">
                <Cog6ToothIcon className="w-6 h-6 text-yellow-400" />
                <h3 className="text-xl font-bold">
                  {isSpanish ? 'Configuración' : 'Settings'}
                </h3>
              </div>
              <button 
                onClick={() => setIsSettingsOpen(false)} 
                className="text-gray-400 hover:text-white transition-colors p-1 rounded-lg"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            {/* Contenido / Opciones */}
            <div className="py-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
                  <LanguageIcon className="w-5 h-5 text-indigo-400" />
                  {isSpanish ? 'Idioma preferido' : 'Preferred Language'}
                </label>
                <p className="text-xs text-gray-400 mb-3">
                  {isSpanish 
                    ? 'Selecciona el idioma por defecto para tu cuenta.' 
                    : 'Choose your account default language.'}
                </p>

                {/* Switchers de Idioma */}
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setSelectedLanguage('es')}
                    className={`flex items-center justify-center gap-2 p-3 rounded-xl border font-semibold text-sm transition-all ${
                      selectedLanguage === 'es'
                        ? 'border-yellow-400 bg-yellow-400/10 text-yellow-300 shadow-lg'
                        : 'border-gray-700 bg-gray-800/60 text-gray-400 hover:border-gray-600'
                    }`}
                  >
                    <span>🇪🇸</span> Español
                  </button>

                  <button
                    type="button"
                    onClick={() => setSelectedLanguage('en')}
                    className={`flex items-center justify-center gap-2 p-3 rounded-xl border font-semibold text-sm transition-all ${
                      selectedLanguage === 'en'
                        ? 'border-yellow-400 bg-yellow-400/10 text-yellow-300 shadow-lg'
                        : 'border-gray-700 bg-gray-800/60 text-gray-400 hover:border-gray-600'
                    }`}
                  >
                    <span>🇺🇸</span> English
                  </button>
                </div>
              </div>

              {/* Mensajes de feedback */}
              {settingsFeedback.message && (
                <div className={`p-3 rounded-lg text-sm flex items-center gap-2 ${
                  settingsFeedback.type === 'success' 
                    ? 'bg-green-500/20 text-green-300 border border-green-500/30' 
                    : 'bg-red-500/20 text-red-300 border border-red-500/30'
                }`}>
                  {settingsFeedback.type === 'success' && <CheckCircleIcon className="w-5 h-5 flex-shrink-0" />}
                  <span>{settingsFeedback.message}</span>
                </div>
              )}
            </div>

            {/* Botones de acción del Modal */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-800">
              <button
                type="button"
                onClick={() => setIsSettingsOpen(false)}
                className="px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors"
                disabled={isSavingSettings}
              >
                {isSpanish ? 'Cancelar' : 'Cancel'}
              </button>
              <button
                type="button"
                onClick={handleSaveSettings}
                disabled={isSavingSettings}
                className="px-5 py-2 text-sm font-semibold rounded-lg bg-yellow-500 text-black hover:bg-yellow-400 disabled:opacity-50 transition-all shadow-md"
              >
                {isSavingSettings 
                  ? (isSpanish ? 'Guardando...' : 'Saving...') 
                  : (isSpanish ? 'Guardar Cambios' : 'Save Changes')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UserMenu;